import React from "react";
import { Link } from "react-router-dom";
import { FaStar, FaThumbsUp, FaEdit, FaTrash, FaCheckCircle, FaGraduationCap, FaTools, FaUser } from "react-icons/fa";

import getImageUrl from "../../utils/imageUtils";
import outcomeDisplayLabel from "../../utils/outcomeLabel";
import StarRatingInput from "./StarRatingInput";
import ProductReviewOwnerFields from "./ProductReviewOwnerFields";
import styles from "../../styles/pages/product/ProductDetailsPage.module.css";

const ProductReviewItem = ({
  review,
  isEditing,
  editReviewForm,
  formConfig = null,
  onEditFormChange,
  onSaveEdit,
  onCancelEdit,
  onHelpful,
  onEditStart,
  onDelete,
  canEdit,
}) => {
  return (
    <div className={styles.reviewItem}>
      {isEditing ? (
        <div className={styles.reviewForm}>
          <h4 className={styles.formTitle}>Edit Your Review</h4>
          <div className={styles.ratingInput}>
            <label>Rating:</label>
            <StarRatingInput
              rating={editReviewForm.rating}
              onChange={(star) =>
                onEditFormChange({ ...editReviewForm, rating: star })
              }
            />
          </div>
          <ProductReviewOwnerFields
            formConfig={formConfig}
            values={editReviewForm}
            onChange={onEditFormChange}
          />
          <div className={styles.commentInput}>
            <label htmlFor="editReviewComment">Your Review:</label>
            <textarea
              id="editReviewComment"
              value={editReviewForm.comment}
              onChange={(e) =>
                onEditFormChange({ ...editReviewForm, comment: e.target.value })
              }
              placeholder="Share your experience with this product..."
              rows={5}
            />
          </div>
          <div className={styles.reviewEditActions}>
            <button className={styles.submitReviewBtn} onClick={onSaveEdit} type="button">
              Save Changes
            </button>
            <button className="buttonSecondary" onClick={onCancelEdit} type="button">
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className={styles.reviewHeader}>
            <div className={styles.reviewUserInfo}>
              {review.user?.avatar ? (
                <img
                  src={getImageUrl(review.user.avatar)}
                  alt="User avatar"
                  className={styles.reviewAvatar}
                />
              ) : (
                <div className={styles.reviewAvatarPlaceholder}>
                  <FaUser />
                </div>
              )}
              <div className={styles.reviewUser}>
                {review.user?._id || review.user ? (
                  <Link
                    to={`/user/${review.user._id || review.user}`}
                    className={styles.reviewAuthorLink}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <strong>
                      {review.user?.username ||
                      (review.user?.firstName && review.user?.lastName)
                        ? `${review.user.firstName} ${review.user.lastName}`
                        : review.user?.email?.split("@")[0] || "Anonymous"}
                    </strong>
                  </Link>
                ) : (
                  <strong>
                    {review.user?.username ||
                    (review.user?.firstName && review.user?.lastName)
                      ? `${review.user.firstName} ${review.user.lastName}`
                      : review.user?.email?.split("@")[0] || "Anonymous"}
                  </strong>
                )}
                <div className={styles.reviewStars}>
                  {[1, 2, 3, 4, 5].map((star) => (
                    <FaStar
                      key={star}
                      className={star <= review.rating ? styles.starFilled : styles.starEmpty}
                      size={14}
                    />
                  ))}
                </div>
              </div>
            </div>
            <div className={styles.reviewMetaActions}>
              <span className={styles.reviewDate}>
                {new Date(review.createdAt).toLocaleDateString()}
              </span>
              {review.helpful !== undefined && review.approved !== false && (
                <button className={styles.helpfulBtn} onClick={onHelpful} type="button">
                  <FaThumbsUp /> Helpful ({review.helpful || 0})
                </button>
              )}
              {canEdit && (
                <div className={styles.reviewEditButtons}>
                  <button
                    className={styles.reviewIconButton}
                    onClick={onEditStart}
                    title="Edit review"
                    type="button"
                  >
                    <FaEdit size={16} />
                  </button>
                  <button
                    className={`${styles.reviewIconButton} ${styles.reviewIconButtonDanger}`}
                    onClick={onDelete}
                    title="Delete review"
                    type="button"
                  >
                    <FaTrash size={16} />
                  </button>
                </div>
              )}
            </div>
          </div>
          <div className={styles.reviewBadges}>
            {review.inSetup && (
              <span className={styles.ownerBadge}>
                <FaTools size={11} /> Owner
              </span>
            )}
            {review.approved === false && (
              <span className={styles.pendingBadge}>Pending approval</span>
            )}
            {review.verifiedPurchase && (
              <span className={styles.verifiedBadge}>
                <FaCheckCircle size={11} /> Owned &amp; Verified
              </span>
            )}
            {review.user?.expert?.status === "approved" && (
              <span className={styles.expertBadge} title={review.user?.expert?.bio || "Verified Expert"}>
                <FaGraduationCap size={11} /> Verified Expert
              </span>
            )}
          </div>
          {(review.variant || review.mainUse || review.ownershipDuration) && (
            <p className={styles.reviewMeta}>
              {[
                review.variant,
                review.mainUse ? `Used for ${review.mainUse}` : null,
                review.ownershipDuration === "over_1_year"
                  ? "Owned over a year"
                  : review.ownershipDuration === "6_12_months"
                    ? "Owned 6–12 months"
                    : null,
              ]
                .filter(Boolean)
                .join(" · ")}
            </p>
          )}
          {Array.isArray(review.outcomeRatings) && review.outcomeRatings.length > 0 && (
            <ul className={styles.reviewOutcomes}>
              {review.outcomeRatings.map((item) => (
                <li key={item.key}>
                  {outcomeDisplayLabel(item.key, formConfig)}: {item.rating}/5
                </li>
              ))}
            </ul>
          )}
          <p className={styles.reviewComment}>{review.comment}</p>
        </>
      )}
    </div>
  );
};

export default ProductReviewItem;
